import { useState, useEffect, useMemo } from "react";
import { format, isSameDay } from "date-fns";
import { Clock, Check } from "lucide-react";
import DateSelector from "./DateSelector";
import SelectedDatesList from "./SelectedDatesList";
import AddressSection from "./AddressSection";
import ScopeSelector from "./ScopeSelector";
import NotesCard from "./NotesCard";
import {
  DateAvailability,
  TimeSlot,
  Address,
  ScopeType,
  DEFAULT_TIME_SLOTS,
} from "./types";
import { CustomButton } from "@/components/ui/Buttons";
import Modal from "@/components/ui/Modal";
import {
  useGetAvailabilityByCreatorIdQuery,
  useUploadSameDayDeliveryDataMutation,
  useUpdateSameDayAvailableCreatorMutation,
} from "@/redux/feature/sameDayDelivery/sameDayDeliveryApi";
import toast from "react-hot-toast";
import { useGlobalContext } from "@/context/GlobalContext";
import EditAvailabilityModal from "./EditAvailabilityModal";

interface AvailabilityModalProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm?: (data: {
    availability: DateAvailability[];
    addressId: string | null;
    scope: ScopeType;
    agreedToTerms: boolean;
  }) => void;
}
const toUtcStartOfDay = (d: Date) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));

const toUtcEndOfDay = (d: Date) =>
  new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999),
  );

const AvailabilityModal = ({
  isOpen,
  setOpen,
  onConfirm,
}: AvailabilityModalProps) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [editingDate, setEditingDate] = useState<DateAvailability | null>(null);

  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [availability, setAvailability] = useState<DateAvailability[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null,
  );
  const [selectedScope, setSelectedScope] = useState<ScopeType>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { userData } = useGlobalContext();

  // Get existing availability data
  const { data: existingDTO, isLoading: isFetchingData } =
    useGetAvailabilityByCreatorIdQuery(userData?.creator_id || "");

  // Fetch the existing availability, if any

  const existingData = existingDTO?.data;
  useEffect(() => {
    if (existingData) {
      setSelectedAddressId(existingData.address_id);
      setSelectedScope(existingData.delivery_scope);
      // Convert existing availability dates to Date objects and set them to selectedDates
      const formattedDates = existingData.availability.map((avail: any) =>
        toUtcStartOfDay(new Date(avail.date)),
      );

      setSelectedDates(formattedDates);

      setAvailability(
        existingData.availability.map((a: any) => ({
          ...a,
          date: toUtcStartOfDay(new Date(a.date)),
        })),
      );
    }
  }, [existingData]);

  const [createSameDayAvailableCreator] =
    useUploadSameDayDeliveryDataMutation();
  const [updateSameDayAvailableCreator] =
    useUpdateSameDayAvailableCreatorMutation();

  const handleDateToggle = (date: Date) => {
    const normalized = toUtcStartOfDay(date);

    const isSelected = selectedDates.some((d) => isSameDay(d, normalized));

    if (isSelected) {
      setSelectedDates(selectedDates.filter((d) => !isSameDay(d, normalized)));
      setAvailability(
        availability.filter((a) => !isSameDay(a.date, normalized)),
      );
    } else {
      setSelectedDates([...selectedDates, normalized]);
      setAvailability([
        ...availability,
        { date: normalized, isAllDay: false, timeSlots: [] },
      ]);
    }
  };

  const handleRemoveDate = (date: Date) => {
    setSelectedDates(selectedDates.filter((d) => !isSameDay(d, date)));
    setAvailability(availability.filter((a) => !isSameDay(a.date, date)));
  };

  const handleToggleAllDay = (date: Date) => {
    setAvailability(
      availability.map((a) =>
        isSameDay(a.date, date)
          ? { ...a, isAllDay: !a.isAllDay, timeSlots: DEFAULT_TIME_SLOTS }
          : a,
      ),
    );
  };

  const handleToggleTimeSlot = ({
    date,
    slot,
    isAllDay,
  }: {
    date: Date;
    isAllDay: boolean;
    slot: TimeSlot[];
  }) => {
    setAvailability(
      availability.map((a) => {
        if (!isSameDay(a.date, date)) return a;

        return {
          ...a,
          isAllDay: isAllDay,
          timeSlots: slot,
        };
      }),
    );
  };

  const handleAddAddress = (address: Omit<Address, "id">) => {
    const newAddress = { ...address, id: Date.now().toString() };
    setSelectedAddressId(newAddress.id);
  };
  const getApiErrorMessage = (err: any) => {
    // RTK Query "fetchBaseQuery" error shape: { status, data }
    const msg =
      err?.data?.message ||
      err?.data?.error ||
      err?.error || // sometimes: "TypeError: Failed to fetch"
      err?.message ||
      "Something went wrong. Please try again.";

    return String(msg);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    const payload = {
      creator_id: userData?.creator_id,
      availability: availability.map((a) => ({
        ...a,
        date: toUtcStartOfDay(new Date(a.date)),
      })),
      address_id: selectedAddressId,
      delivery_scope: selectedScope,
      agreedToTerms,
    };

    try {
      if (existingData) {
        // If data exists, we update the availability
        await updateSameDayAvailableCreator(payload).unwrap();
        toast.success("Availability updated successfully");
      } else {
        // If no data exists, we create a new SameDayAvailableCreator
        await createSameDayAvailableCreator(payload).unwrap();
        toast.success("Availability created successfully");
      }

      setIsLoading(false);
      setOpen(false);
    } catch (error: any) {
      console.error("Availability submit error:", error);

      const msg = getApiErrorMessage(error);

      // show the actual backend message like "Address not found"
      toast.error(msg);

      setIsLoading(false);
    }
  };

  const isValid = useMemo(() => {
    const hasValidAvailability = availability?.some(
      (a) => a.isAllDay || a.timeSlots.length > 0,
    );
    return hasValidAvailability && selectedScope && agreedToTerms;
  }, [availability, selectedScope, agreedToTerms]);

  return (
    <Modal isOpen={isOpen} setOpen={setOpen} width="560px">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
            <Clock className="w-5 h-5 text-accent" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-foreground">
                Confirm your Availability
              </h2>
              <span className="px-3 py-1 rounded-full border border-accent text-accent text-xs font-medium">
                {format(today, "EEE, do MMMM")}
              </span>
            </div>
          </div>
        </div>

        {/* Subheading */}
        <p className="text-sm text-muted-foreground leading-relaxed">
          Select the time slots you're available today to receive same-day
          projects. You'll only get client briefs during the slots you confirm.
        </p>

        {/* Date Selector */}
        <DateSelector
          selectedDates={selectedDates}
          onDateToggle={handleDateToggle}
        />

        {/* Selected Dates with Time Slots */}
        <SelectedDatesList
          availability={availability}
          onUpdateAvailability={(vl) => {
            console.log(vl);
          }}
          setEditingDate={setEditingDate}
          // onToggleAllDay={handleToggleAllDay}
          // onUpdateAvailability={}
          // onToggleTimeSlot={handleToggleTimeSlot}
          onRemoveDate={handleRemoveDate}
        />
        {editingDate && (
          <EditAvailabilityModal
            data={editingDate}
            isOpen={editingDate ? true : false}
            onClose={() => setEditingDate(null)}
            onSave={(updated) => {
              // onUpdateAvailability(updated);
              console.log(updated);
              handleToggleTimeSlot({
                slot: updated.timeSlots,
                isAllDay: updated.isAllDay,
                date: toUtcStartOfDay(updated.date),
              });

              setEditingDate(null);
            }}
          />
        )}
        {/* Address Section */}
        <AddressSection
          selectedAddressId={selectedAddressId}
          onSelectAddress={setSelectedAddressId}
          onAddAddress={handleAddAddress}
        />

        {/* Notes */}
        <NotesCard />

        {/* Scope Selector */}
        <ScopeSelector
          selectedScope={selectedScope}
          onSelectScope={setSelectedScope}
          shootAndEditPrice={2200}
          shootOnlyPrice={1800}
        />

        {/* Terms Agreement */}
        <button
          onClick={() => setAgreedToTerms(!agreedToTerms)}
          className="flex items-center gap-3 text-sm"
        >
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
              agreedToTerms
                ? "bg-accent border-accent"
                : "border-border bg-card"
            }`}
          >
            {agreedToTerms && (
              <Check className="w-3 h-3 text-accent-foreground" />
            )}
          </div>
          <span className="text-muted-foreground">
            I agree to the{" "}
            <span className="text-foreground underline underline-offset-2 font-medium">
              T&C
            </span>
          </span>
        </button>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <CustomButton
            variant="outline"
            className="flex-1"
            onClick={() => setOpen(false)}
          >
            Cancel
          </CustomButton>
          <CustomButton
            className="flex-1 bg-primary hover:bg-primary/90"
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
          >
            {existingData ? (
              <>{isLoading ? "Updating..." : "Update Slots"}</>
            ) : (
              <>{isLoading ? "Confirming..." : "Confirm Slots"}</>
            )}
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default AvailabilityModal;
